<script setup lang="ts">
import AppStatusTag from './StatusTag.vue'

type EntityHeaderTone = 'default' | 'primary' | 'success' | 'warning' | 'danger'

interface EntityHeaderMetaItem {
  label: string
  value: string | number
}

interface EntityHeaderSummaryItem {
  label: string
  value: string | number
  tone?: EntityHeaderTone
}

defineOptions({
  name: 'AppEntityHeader',
})

const props = withDefaults(defineProps<{
  title: string
  subtitle?: string
  statusText?: string
  statusTone?: EntityHeaderTone
  metaItems?: EntityHeaderMetaItem[]
  summaryItems?: EntityHeaderSummaryItem[]
  tags?: string[]
}>(), {
  subtitle: '',
  statusText: '',
  statusTone: 'default',
  metaItems: () => [],
  summaryItems: () => [],
  tags: () => [],
})

function getSummaryClass(tone: EntityHeaderTone = 'default') {
  if (tone === 'primary') {
    return 'border-primary/20 bg-primary/4 text-primary'
  }
  if (tone === 'success') {
    return 'border-success/20 bg-success/4 text-success'
  }
  if (tone === 'warning') {
    return 'border-warning/20 bg-warning/4 text-warning'
  }
  if (tone === 'danger') {
    return 'border-danger/20 bg-danger/4 text-danger'
  }
  return 'border-default bg-subtle text-foreground'
}
</script>

<template>
  <div class="border-default bg-default p-4 border rounded-3">
    <div class="flex flex-wrap gap-4 items-start justify-between">
      <div class="flex-1 min-w-0">
        <div class="flex flex-wrap gap-2 items-center">
          <div class="text-xl leading-7 font-semibold">
            {{ props.title }}
          </div>
          <AppStatusTag
            v-if="props.statusText"
            :label="props.statusText"
            :tone="props.statusTone"
          />
          <FaTag v-for="tag in props.tags" :key="tag" variant="outline">
            {{ tag }}
          </FaTag>
        </div>

        <div v-if="props.subtitle" class="text-sm text-muted-foreground leading-6 mt-2">
          {{ props.subtitle }}
        </div>

        <div v-if="props.metaItems.length" class="mt-4 gap-3 grid sm:grid-cols-2 xl:grid-cols-4">
          <div
            v-for="item in props.metaItems"
            :key="item.label"
            class="border-default bg-subtle px-3 py-3 border rounded-3"
          >
            <div class="text-xs text-muted-foreground">
              {{ item.label }}
            </div>
            <div class="text-sm font-medium mt-1 truncate">
              {{ item.value }}
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-3 min-w-0 items-stretch xl:min-w-72">
        <slot name="actions" />

        <div v-if="props.summaryItems.length" class="gap-3 grid sm:grid-cols-2 xl:grid-cols-1">
          <div
            v-for="item in props.summaryItems"
            :key="item.label"
            class="px-4 py-3 border rounded-3"
            :class="getSummaryClass(item.tone)"
          >
            <div class="text-xs opacity-80">
              {{ item.label }}
            </div>
            <div class="text-2xl leading-none font-semibold mt-1 tabular-nums">
              {{ item.value }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
